import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useChartContext, InternalChartType } from '@shopify/polaris-viz-core';
import { createPortal } from 'react-dom';
import { useRootContainer } from '../../hooks/useRootContainer.js';
import { TOOLTIP_ID } from '../../constants.js';
import { getHorizontalBarChartTooltipPosition } from './utilities/getHorizontalBarChartTooltipPosition.js';
import { getLineChartTooltipPosition } from './utilities/getLineChartTooltipPosition.js';
import { getVerticalBarChartTooltipPosition } from './utilities/getVerticalBarChartTooltipPosition.js';
import { shouldBlockTooltipEvents } from './utilities/shouldBlockTooltipEvents.js';
import { DEFAULT_TOOLTIP_POSITION } from './constants.js';
import { TooltipAnimatedContainer } from './components/TooltipAnimatedContainer.js';
import { getDonutChartTooltipPosition } from './utilities/getDonutChartTooltipPosition.js';
import { jsx } from 'react/jsx-runtime';
import { SwallowErrors } from '../SwallowErrors/SwallowErrors.js';

const TOUCH_START_DELAY = 300;
function TooltipWrapperRaw(props) {
  const {
    bandwidth = 0,
    chartBounds,
    chartType,
    data,
    forceActiveIndex,
    focusElementDataType,
    id,
    longestSeriesIndex,
    onIndexChange,
    parentElement,
    type,
    xScale,
    yScale
  } = props;
  const {
    scrollContainer,
    isTouchDevice,
    containerBounds
  } = useChartContext();
  const [position, setPosition] = useState({
    x: 0,
    y: 0,
    activeIndex: -1,
    position: DEFAULT_TOOLTIP_POSITION
  });
  const activeIndexRef = useRef(null);
  const touchStartTimer = useRef(0);
  const isLongTouch = useRef(false);
  const focusElements = useMemo(() => {
    return parentElement === null || parentElement === void 0 ? void 0 : parentElement.querySelectorAll(`[data-type="${focusElementDataType}"][aria-hidden="false"]`);
  }, [focusElementDataType, parentElement]);
  useEffect(() => {
    activeIndexRef.current = position.activeIndex;
  }, [position.activeIndex]);
  const alwaysUpdatePosition = [InternalChartType.Line, InternalChartType.Donut].includes(chartType) && !isTouchDevice;
  const getPosition = useCallback(({
    event,
    eventType,
    index
  }) => {
    var _ref;
    const scrollY = scrollContainer == null ? 0 : scrollContainer.scrollTop;
    switch (chartType) {
      case InternalChartType.Line:
        return getLineChartTooltipPosition({
          chartBounds,
          containerBounds,
          scrollY,
          data: data !== null && data !== void 0 ? data : [],
          event,
          eventType,
          index,
          isTouchDevice,
          longestSeriesIndex: longestSeriesIndex !== null && longestSeriesIndex !== void 0 ? longestSeriesIndex : 0,
          xScale: xScale
        });
      case InternalChartType.HorizontalBar:
        return getHorizontalBarChartTooltipPosition({
          chartBounds,
          data: data !== null && data !== void 0 ? data : [],
          event,
          eventType,
          index,
          longestSeriesIndex: longestSeriesIndex !== null && longestSeriesIndex !== void 0 ? longestSeriesIndex : 0,
          type,
          xScale: xScale
        });
      case InternalChartType.Donut:
        return getDonutChartTooltipPosition({
          containerBounds,
          event,
          eventType,
          index: (_ref = index !== null && index !== void 0 ? index : forceActiveIndex) !== null && _ref !== void 0 ? _ref : undefined,
          parentElement
        });
      case InternalChartType.Bar:
      default:
        return getVerticalBarChartTooltipPosition({
          chartBounds,
          containerBounds,
          data: data !== null && data !== void 0 ? data : [],
          event,
          eventType,
          index,
          longestSeriesIndex: longestSeriesIndex !== null && longestSeriesIndex !== void 0 ? longestSeriesIndex : 0,
          type,
          xScale: xScale,
          yScale: yScale
        });
    }
  }, [chartBounds, containerBounds, chartType, data, forceActiveIndex, longestSeriesIndex, isTouchDevice, type, xScale, yScale, scrollContainer, parentElement]);
  const showAndPositionTooltip = useCallback(event => {
    var _scrollContainer$scro;
    const newPosition = getPosition({
      event,
      eventType: 'mouse'
    });
    const scrollContainerTop = Number((_scrollContainer$scro = scrollContainer === null || scrollContainer === void 0 ? void 0 : scrollContainer.scrollTop) !== null && _scrollContainer$scro !== void 0 ? _scrollContainer$scro : 0);
    const y = newPosition.y + scrollContainerTop;
    if (alwaysUpdatePosition && (newPosition.x < chartBounds.x || y < chartBounds.y)) {
      return;
    }
    if (!alwaysUpdatePosition && activeIndexRef.current === newPosition.activeIndex) {
      return;
    }
    if (shouldBlockTooltipEvents(event)) {
      return;
    }
    setPosition(newPosition);
    onIndexChange === null || onIndexChange === void 0 ? void 0 : onIndexChange(newPosition.activeIndex);
  }, [alwaysUpdatePosition, chartBounds, getPosition, onIndexChange, scrollContainer]);
  const onMouseMove = useCallback(event => {
    window.clearTimeout(touchStartTimer.current);
    if (typeof TouchEvent !== 'undefined' && event instanceof TouchEvent) {
      if (isLongTouch.current === true) {
        // prevents scrolling after long touch (since it is supposed to move the tooltip/datapoint vs scroll)
        event === null || event === void 0 ? void 0 : event.preventDefault();
      } else {
        return;
      }
    }
    showAndPositionTooltip(event);
  }, [showAndPositionTooltip]);
  const onMouseLeave = useCallback(() => {
    isLongTouch.current = false;
    window.clearTimeout(touchStartTimer.current);
    onIndexChange === null || onIndexChange === void 0 ? void 0 : onIndexChange(null);
    setPosition(prevState => {
      return {
        ...prevState,
        activeIndex: -1
      };
    });
  }, [onIndexChange]);
  const onTouchStart = useCallback(event => {
    touchStartTimer.current = window.setTimeout(() => {
      event.preventDefault();
      isLongTouch.current = true;
      showAndPositionTooltip(event);
    }, TOUCH_START_DELAY);
  }, [showAndPositionTooltip]);
  const onFocus = useCallback(event => {
    const target = event.currentTarget;
    if (!target) {
      return;
    }
    const index = Number(target.dataset.index);
    const newPosition = getPosition({
      event,
      index,
      eventType: 'focus'
    });
    setPosition(newPosition);
    onIndexChange === null || onIndexChange === void 0 ? void 0 : onIndexChange(newPosition.activeIndex);
  }, [getPosition, onIndexChange]);
  const onFocusIn = useCallback(() => {
    if (!(parentElement !== null && parentElement !== void 0 && parentElement.contains(document.activeElement))) {
      onMouseLeave();
    }
  }, [parentElement, onMouseLeave]);
  const setFocusListeners = useCallback(attach => {
    if (!focusElements) {
      return;
    }
    focusElements.forEach(el => {
      if (attach) {
        el.addEventListener('focus', onFocus);
      } else {
        el.removeEventListener('focus', onFocus);
      }
    });
  }, [focusElements, onFocus]);
  useEffect(() => {
    if (!parentElement) {
      return;
    }
    parentElement.addEventListener('mousemove', onMouseMove);
    parentElement.addEventListener('mouseleave', onMouseLeave);
    parentElement.addEventListener('touchstart', onTouchStart);
    parentElement.addEventListener('touchmove', onMouseMove);
    parentElement.addEventListener('touchend', onMouseLeave);
    setFocusListeners(true);
    return () => {
      parentElement.removeEventListener('mousemove', onMouseMove);
      parentElement.removeEventListener('mouseleave', onMouseLeave);
      parentElement.removeEventListener('touchstart', onTouchStart);
      parentElement.removeEventListener('touchmove', onMouseMove);
      parentElement.removeEventListener('touchend', onMouseLeave);
      setFocusListeners(false);
    };
  }, [parentElement, onMouseLeave, onTouchStart, setFocusListeners, onMouseMove]);
  useEffect(() => {
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [parentElement, onFocusIn]);
  if (position.activeIndex == null || position.activeIndex < 0) {
    return null;
  }
  return /*#__PURE__*/jsx(TooltipAnimatedContainer, {
    bandwidth: bandwidth,
    chartBounds: chartBounds,
    chartType: chartType,
    currentX: position.x,
    currentY: position.y,
    id: id,
    margin: props.margin,
    position: position.position,
    children: props.getMarkup(position.activeIndex)
  });
}
function TooltipWrapper({
  usePortal = false,
  ...props
}) {
  if (usePortal) {
    return /*#__PURE__*/jsx(TooltipWithPortal, {
      ...props
    });
  }
  return /*#__PURE__*/jsx(TooltipWithErrors, {
    ...props
  });
}
function TooltipWithErrors(props) {
  return /*#__PURE__*/jsx(SwallowErrors, {
    children: /*#__PURE__*/jsx(TooltipWrapperRaw, {
      ...props
    })
  });
}
function TooltipWithPortal(props) {
  const container = useRootContainer(TOOLTIP_ID);
  return /*#__PURE__*/createPortal( /*#__PURE__*/jsx(TooltipWithErrors, {
    ...props
  }), container);
}

export { TooltipWrapper };
